/**
 * Queries do WHATSAPP (v6): integração (api key/webhook), config pública do
 * Embedded Signup (appId/configIds do FB) e canais (números conectados).
 * Endpoints admin-only — o 403 vira mensagem amigável via errorMessage.
 */
import { queryOptions } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import type { WhatsappChannel, WhatsappConfig, WhatsappIntegration } from '@erp/shared';
import { errorMessage as genericErrorMessage } from '@/features/tickets/ticket-format';

export const WHATSAPP_KEY = ['erp', 'whatsapp'] as const;

export function whatsappIntegrationOptions() {
  return queryOptions({
    queryKey: [...WHATSAPP_KEY, 'integration'] as const,
    queryFn: async ({ signal }) => {
      const res = await api<WhatsappIntegration>('/whatsapp/integration', { signal });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

/** Config pública-staff do signup (appId + config ids do FB) — estática. */
export function whatsappConfigOptions() {
  return queryOptions({
    queryKey: [...WHATSAPP_KEY, 'config'] as const,
    queryFn: async ({ signal }) => {
      const res = await api<WhatsappConfig>('/whatsapp/config', { signal });
      if (res.error) throw res.error;
      return res.data;
    },
    staleTime: Infinity,
  });
}

export function whatsappChannelsOptions() {
  return queryOptions({
    queryKey: [...WHATSAPP_KEY, 'channels'] as const,
    queryFn: async ({ signal }) => {
      const res = await api<WhatsappChannel[]>('/whatsapp/channels', { signal });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

/** Códigos de erro da API com mensagem amigável em pt-BR. */
const ERROR_LABELS: Record<string, string> = {
  forbidden: 'Apenas administradores podem alterar a integração do WhatsApp.',
  whatsapp_not_configured: 'Configure a API key da organização antes de continuar.',
  whatsapp_manager_error:
    'O manager do zeepchat não respondeu — verifique a API key e tente de novo.',
  desk_not_found: 'A mesa selecionada não existe mais — recarregue a página e tente de novo.',
  // chatbot (v8 Etapa 4): menu com deskId de mesa inexistente — e, na 021, o
  // mapeamento de filas do bot externo
  unknown_desk_ids:
    'Uma das mesas escolhidas não existe mais (ou foi desativada) — recarregue a página e tente de novo.',
  // spec 021: bot externo
  duplicate_bot_queue: 'Uma fila aparece duas vezes no mapeamento — cada fila vai para uma mesa só.',
  bot_mode_conflict: 'O modo do bot e o chatbot do ERP vieram diferentes — recarregue a página e salve de novo.',
};

/** Códigos locais primeiro; os transversais (permission_denied…) vêm do
 *  fallback genérico do ticket-format — fonte única do wording. */
export function errorMessage(err: unknown): string {
  if (
    err !== null &&
    typeof err === 'object' &&
    'error' in err &&
    typeof (err as { error: unknown }).error === 'string'
  ) {
    const label = ERROR_LABELS[(err as { error: string }).error];
    if (label) return label;
  }
  return genericErrorMessage(err);
}
